import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { captureException } from '@sentry/react';
import type { PluginKey } from '@tiptap/pm/state';
import type { SuggestionProps } from '@tiptap/suggestion';
import { exitSuggestion } from '@tiptap/suggestion';
import { Input } from 'antd';
import type { InputRef } from 'antd';
import { capitalCase } from 'change-case';
import { matchSorter } from 'match-sorter';
import { TbAt, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { resourceCommentService } from '@/services/database';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { ListItemSkeleton } from '@/shared/components/skeletons';
import type { CommentMentionOptionGroups, CommentMentionTarget, ResourceActivityResourceType } from '@/types';
import { formatDate } from '@/utilities';

import CommentMentionIdentity from '../CommentMentionIdentity';

export type MentionSuggestionListHandle = {
    onKeyDown: (event: KeyboardEvent) => boolean;
};

type MentionSuggestionListProps = SuggestionProps<CommentMentionTarget, CommentMentionTarget> & {
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    suggestionPluginKey?: PluginKey;
};

type MentionSection = {
    items: CommentMentionTarget[];
    key: keyof CommentMentionOptionGroups;
    label: string;
};

const emptyOptions: CommentMentionOptionGroups = { people: [], resources: [], teams: [] };

const MentionSuggestionList = forwardRef<MentionSuggestionListHandle, MentionSuggestionListProps>((props, ref) => {
    const { token } = useAntDesignTheme();
    const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
    const popupRef = useRef<HTMLDivElement>(null);
    const requestIdRef = useRef(0);
    const searchInputRef = useRef<InputRef>(null);
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<CommentMentionOptionGroups>(emptyOptions);
    const [loading, setLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        const animationFrame = window.requestAnimationFrame(() => searchInputRef.current?.focus());

        return () => window.cancelAnimationFrame(animationFrame);
    }, []);

    useEffect(() => {
        let active = true;
        const timeout = window.setTimeout(
            () => {
                const requestId = ++requestIdRef.current;

                setLoading(true);
                resourceCommentService
                    .mentionOptions(props.resourceType, props.resourceId, query)
                    .then((nextOptions) => {
                        if (!active || requestId !== requestIdRef.current) {
                            return;
                        }

                        setOptions(nextOptions);
                        setSelectedIndex(0);
                    })
                    .catch((error) => {
                        if (!active || requestId !== requestIdRef.current) {
                            return;
                        }

                        captureException(error);
                        setOptions(emptyOptions);
                        setSelectedIndex(0);
                    })
                    .finally(() => {
                        if (active && requestId === requestIdRef.current) {
                            setLoading(false);
                        }
                    });
            },
            query ? 140 : 0,
        );

        return () => {
            active = false;
            window.clearTimeout(timeout);
        };
    }, [props.resourceId, props.resourceType, query]);

    const sections = useMemo<MentionSection[]>(() => {
        const filtered = (items: CommentMentionTarget[]) =>
            query
                ? matchSorter(items, query, {
                      keys: ['label', 'handle'],
                      threshold: matchSorter.rankings.MATCHES,
                  })
                : items;

        const recentResources = [...options.resources].sort((first, second) => {
            const firstUpdatedAt = first.updatedAt ? new Date(first.updatedAt).getTime() : 0;
            const secondUpdatedAt = second.updatedAt ? new Date(second.updatedAt).getTime() : 0;

            return secondUpdatedAt - firstUpdatedAt;
        });

        return [
            { items: filtered(options.people), key: 'people', label: 'People' },
            { items: filtered(options.teams), key: 'teams', label: 'Teams' },
            { items: filtered(recentResources), key: 'resources', label: 'Workspace Resources' },
        ].filter((section) => section.items.length > 0) as MentionSection[];
    }, [options, query]);
    const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);

    useEffect(() => {
        itemRefs.current.get(selectedIndex)?.scrollIntoView?.({
            block: 'nearest',
            inline: 'nearest',
        });
    }, [flatItems.length, selectedIndex]);

    const selectItem = (index: number) => {
        const item = flatItems[index];

        if (!item) {
            return;
        }

        if (popupRef.current) {
            popupRef.current.style.visibility = 'hidden';
        }
        props.command(item);
    };

    const handleKeyDown = (event: KeyboardEvent | ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            exitSuggestion(props.editor.view, props.suggestionPluginKey);
            props.editor.commands.focus();
            return true;
        }

        if (!flatItems.length) {
            return false;
        }

        if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
            event.preventDefault();
            setSelectedIndex((current) => (current + 1) % flatItems.length);
            return true;
        }

        if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
            event.preventDefault();
            setSelectedIndex((current) => (current + flatItems.length - 1) % flatItems.length);
            return true;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            selectItem(selectedIndex);
            return true;
        }

        return false;
    };

    useImperativeHandle(ref, () => ({ onKeyDown: handleKeyDown }));

    let itemIndex = -1;

    return (
        <div
            ref={popupRef}
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                maxHeight: '22rem',
                overflow: 'hidden',
                width: '21rem',
                zIndex: 11000,
            }}
        >
            <div style={{ borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: '0.5rem' }}>
                <Input
                    allowClear
                    ref={searchInputRef}
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setSelectedIndex(0);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder='Search By Name Or Handle...'
                    prefix={
                        <TbSearch size='1rem' style={{ color: token.colorTextQuaternary, marginRight: '0.325rem' }} />
                    }
                />
            </div>
            <OverflowFadeScrollArea
                aria-label='Mention suggestions'
                containerStyle={{ maxHeight: '18rem' }}
                style={{ padding: '0.375rem' }}
                surface='elevated'
            >
                {loading && sections.length === 0 ? (
                    <div aria-label='Loading mention suggestions' style={{ padding: '0.25rem 0.375rem' }}>
                        {Array.from({ length: 4 }, (_, index) => (
                            <ListItemSkeleton
                                avatar
                                compact
                                key={index}
                                lineWidths={['64%', '42%']}
                                style={{ padding: '0.5rem 0' }}
                            />
                        ))}
                    </div>
                ) : sections.length ? (
                    sections.map((section, sectionIndex) => (
                        <div
                            key={section.key}
                            style={{
                                borderTop: sectionIndex > 0 ? `1px solid ${token.colorBorderSecondary}` : undefined,
                                marginTop: sectionIndex > 0 ? '0.375rem' : 0,
                                paddingTop: sectionIndex > 0 ? '0.375rem' : 0,
                            }}
                        >
                            <div style={{ padding: '0.125rem 0.375rem 0.375rem' }}>
                                <small style={{ color: token.colorTextTertiary }}>{section.label}</small>
                            </div>
                            {section.items.map((item) => {
                                itemIndex += 1;
                                const currentIndex = itemIndex;
                                const selected = currentIndex === selectedIndex;
                                const handle = item.handle
                                    ? item.handle.includes('@')
                                        ? item.handle
                                        : `@${item.handle}`
                                    : undefined;
                                const description =
                                    item.kind === 'resource'
                                        ? item.updatedAt
                                            ? `Updated ${formatDate(new Date(item.updatedAt), 'mini')}`
                                            : capitalCase(item.resourceType ?? 'workspace resource')
                                        : handle;

                                return (
                                    <button
                                        key={item.key}
                                        ref={(element) => {
                                            if (element) {
                                                itemRefs.current.set(currentIndex, element);
                                            } else {
                                                itemRefs.current.delete(currentIndex);
                                            }
                                        }}
                                        type='button'
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            selectItem(currentIndex);
                                        }}
                                        onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        style={{
                                            alignItems: 'flex-start',
                                            background: selected ? token.colorFillTertiary : 'transparent',
                                            border: 0,
                                            borderRadius: token.borderRadiusSM,
                                            color: token.colorText,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            gap: '0.5rem',
                                            minHeight: '2.25rem',
                                            padding: '0.375rem 0.5rem',
                                            textAlign: 'left',
                                            width: '100%',
                                        }}
                                    >
                                        <CommentMentionIdentity
                                            avatarSize={20}
                                            description={description}
                                            descriptionStyle={{ color: token.colorTextTertiary, lineHeight: 1.35 }}
                                            labelStyle={{ fontWeight: 500, lineHeight: 1.35 }}
                                            mention={item}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    ))
                ) : (
                    <EmptySection
                        description='No mentionable people, teams, or workspace resources found.'
                        height='12rem'
                        icon={<TbAt size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                        label='No Mentions Found'
                    />
                )}
            </OverflowFadeScrollArea>
        </div>
    );
});

MentionSuggestionList.displayName = 'MentionSuggestionList';

export default MentionSuggestionList;
